import { type MouseEvent, useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { type DialogState } from 'ariakit'
import {
  Anchor,
  Button,
  csx,
  IconCloudArrowUp,
  ModalButton,
  ModalContent,
  ModalFooter,
  Paragraph,
  Stack,
  Text,
} from '@vtex/admin-ui'
import { useAdmin } from '@vtex/raccoon-next'
import { CustomModal } from '@/components/CustomModal'
import { type ImportMenuProps } from '@/components/products-listing/components/ImportMenu/ImportMenu'
import {
  UploadFileArea,
  type UploadFileAreaRef,
} from '@/components/products-listing/components/UploadFileArea/UploadFileArea'
import { useImportTemplate } from '@/components/products-listing/hooks/useImportRequest'
import { resolveLocale } from '@/lib/resolveLocale'

import * as style from './ImportModal.styles'

export type ImportModalProps = {
  state: DialogState
} & Omit<ImportMenuProps, 'importResponse'>

export const ImportModal = ({
  importType,
  state,
  postImportRequest,
  hasFile,
  setHasFile,
}: ImportModalProps) => {
  const { $t } = useIntl()
  const { getImportTemplate } = useImportTemplate()

  const { locale = '' } = useAdmin()
  const resolvedLocale = resolveLocale(locale)

  const uploadInputRef = useRef<UploadFileAreaRef>(null)

  const allowedTypes = [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ]
  const isCorrectFormat =
    allowedTypes.includes(hasFile?.type ?? '') &&
    !!hasFile?.name.match(/\.(xls|xlsx)$/i)

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    uploadInputRef.current?.openUploader()
    event.preventDefault()
  }

  const generateTitle = (type: string) => {
    const parseType: Record<string, string> = {
      Product: $t({
        id: 'admin/admin.catalog.import-modal.products-and-skus-title',
        defaultMessage: 'Import products and SKUs',
      }),
      Image: $t({
        id: 'admin/admin.catalog.import-modal.images-title',
        defaultMessage: 'Import images',
      }),
      ProductSpecification: $t({
        id: 'admin/admin.catalog.import-modal.product-specifications-title',
        defaultMessage: 'Import product specifications',
      }),
      SkuSpecification: $t({
        id: 'admin/admin.catalog.import-modal.sku-specifications-title',
        defaultMessage: 'Import SKU specifications',
      }),
    }
    return parseType[type]
  }

  const generateHelpCenterLink = (importType: string) => {
    const slugs: Record<string, Record<string, string>> = {
      Product: {
        pt: 'importar-e-exportar-produtos-e-skus-via-planilha',
        es: 'importar-y-exportar-productos-y-skus-mediante-plantilla',
        en: 'importing-and-exporting-products-and-skus-using-a-spreadsheet',
      },
      Image: {
        pt: 'importar-e-exportar-imagens-de-produtos-e-skus-via-planilha',
        es: 'importar-y-exportar-imagenes-de-productos-y-skus-mediante-plantilla',
        en: 'importing-and-exporting-product-and-sku-images-using-a-spreadsheet',
      },
      ProductSpecification: {
        pt: 'importar-e-exportar-especificacoes-de-produtos-via-planilha',
        es: 'importar-y-exportar-especificaciones-de-productos-mediante-plantilla',
        en: 'importing-and-exporting-product-specifications-using-a-spreadsheet',
      },
      SkuSpecification: {
        pt: 'importar-e-exportar-especificacoes-de-skus-via-planilha',
        es: 'importar-y-exportar-especificaciones-de-skus-mediante-plantilla',
        en: 'importing-and-exporting-sku-specifications-using-a-spreadsheet',
      },
    }

    const slug = slugs[importType]?.[resolvedLocale] ?? slugs[importType]?.en

    if (resolvedLocale === 'en') {
      return `https://help.vtex.com/docs/tutorials/${slug}`
    }

    return `https://help.vtex.com/${resolvedLocale}/docs/tutorials/${slug}`
  }

  return (
    <CustomModal title={generateTitle(importType)} state={state} size="medium">
      <ModalContent
        className={csx({
          paddingTop: '0',
        })}
      >
        <UploadFileArea
          ref={uploadInputRef}
          modalState={state}
          handleClick={handleClick}
          hasFile={hasFile}
          setHasFile={setHasFile}
          isCorrectFormat={isCorrectFormat}
          innerButton={
            <Button
              aria-label="Import file"
              variant="neutralTertiary"
              onClick={(event) => {
                handleClick(event)
              }}
            >
              <Stack direction="row" space="$space-1">
                <IconCloudArrowUp className="menu-button__icon" />
                <Text className="menu-button__text">
                  {$t({
                    id: 'admin/admin.catalog.import-modal.import-file-label',
                    defaultMessage: 'Add an XLS or XLSX file',
                  })}
                </Text>
              </Stack>
            </Button>
          }
        />
        {importType === 'Image' || importType === 'Product' ? (
          <Paragraph className={style.modalInfo}>
            <FormattedMessage
              id="admin/admin.catalog.import-modal.informative-description"
              defaultMessage="Download <link1>template</link1>. <link2>Learn more</link2>"
              values={{
                link1: (text) => (
                  <Anchor
                    target="_blank"
                    onClick={() => getImportTemplate(importType)}
                  >
                    {text}
                  </Anchor>
                ),
                link2: (text) => (
                  <Anchor
                    target="_blank"
                    href={generateHelpCenterLink(importType)}
                  >
                    {text}
                  </Anchor>
                ),
              }}
            />
          </Paragraph>
        ) : (
          <Paragraph className={style.modalInfo}>
            <FormattedMessage
              id="admin/admin.catalog.import-modal.lean-more-cta"
              defaultMessage="<link1>Learn more</link1>"
              values={{
                link1: (text) => (
                  <Anchor
                    target="_blank"
                    href={generateHelpCenterLink(importType)}
                  >
                    {text}
                  </Anchor>
                ),
              }}
            />
          </Paragraph>
        )}
      </ModalContent>
      <ModalFooter className={style.modalAction}>
        <ModalButton
          onClick={() => {
            if (hasFile) postImportRequest(importType, hasFile)
            state.hide()
          }}
          size="large"
          className={csx({ minWidth: '104px' })}
          disabled={!hasFile || !isCorrectFormat}
        >
          {$t({
            id: 'admin/admin.catalog.import-modal.import-button-label',
            defaultMessage: 'Import',
          })}
        </ModalButton>
      </ModalFooter>
    </CustomModal>
  )
}
